import { useState, useEffect, useCallback } from "react";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  Lightbulb,
  Thermometer,
  Lock,
  Unlock,
  Sun,
  Moon,
  Snowflake,
  Flame,
  Power,
} from "lucide-react";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";

export default function AutomationPage() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDevices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/devices`);
      setDevices(res.data);
    } catch (error) {
      toast.error("Failed to fetch devices");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const updateDeviceState = async (deviceId, newState) => {
    try {
      const res = await axios.put(`${API}/devices/${deviceId}/state`, {
        state: newState,
      });
      setDevices((prev) =>
        prev.map((d) => (d.id === deviceId ? res.data : d))
      );
    } catch (error) {
      toast.error("Failed to update device");
    }
  };

  const lights = devices.filter((d) => d.type === "light");
  const thermostats = devices.filter((d) => d.type === "thermostat");
  const locks = devices.filter((d) => d.type === "lock");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="automation-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white font-['Outfit']">
          Home Control
        </h1>
        <p className="text-slate-400 mt-1">
          Manage your smart home devices
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <button
          onClick={() => {
            lights.forEach((light) => {
              if (!light.state.on) {
                updateDeviceState(light.id, { ...light.state, on: true });
              }
            });
            toast.success("All lights turned on");
          }}
          className="card-base p-4 flex flex-col items-center gap-2 hover:bg-slate-800"
          data-testid="all-lights-on"
        >
          <Sun className="w-8 h-8 text-yellow-400" />
          <span className="text-sm text-slate-300">All Lights On</span>
        </button>
        <button
          onClick={() => {
            lights.forEach((light) => {
              if (light.state.on) {
                updateDeviceState(light.id, { ...light.state, on: false });
              }
            });
            toast.success("All lights turned off");
          }}
          className="card-base p-4 flex flex-col items-center gap-2 hover:bg-slate-800"
          data-testid="all-lights-off"
        >
          <Moon className="w-8 h-8 text-slate-500" />
          <span className="text-sm text-slate-300">All Lights Off</span>
        </button>
        <button
          onClick={() => {
            locks.forEach((lock) => {
              if (!lock.state.locked) {
                updateDeviceState(lock.id, { locked: true });
              }
            });
            toast.success("All doors locked");
          }}
          className="card-base p-4 flex flex-col items-center gap-2 hover:bg-slate-800"
          data-testid="lock-all"
        >
          <Lock className="w-8 h-8 text-red-400" />
          <span className="text-sm text-slate-300">Lock All</span>
        </button>
        <button
          onClick={() => {
            locks.forEach((lock) => {
              if (lock.state.locked) {
                updateDeviceState(lock.id, { locked: false });
              }
            });
            toast.success("All doors unlocked");
          }}
          className="card-base p-4 flex flex-col items-center gap-2 hover:bg-slate-800"
          data-testid="unlock-all"
        >
          <Unlock className="w-8 h-8 text-green-400" />
          <span className="text-sm text-slate-300">Unlock All</span>
        </button>
      </div>

      {/* Lights Section */}
      <section>
        <h2 className="text-xl font-semibold text-white font-['Outfit'] mb-4 flex items-center gap-2">
          <Lightbulb className="text-yellow-400" size={24} />
          Lights
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {lights.map((light) => (
            <LightCard
              key={light.id}
              device={light}
              onUpdate={(newState) => updateDeviceState(light.id, newState)}
            />
          ))}
        </div>
      </section>

      {/* Thermostat Section */}
      <section>
        <h2 className="text-xl font-semibold text-white font-['Outfit'] mb-4 flex items-center gap-2">
          <Thermometer className="text-cyan-400" size={24} />
          Climate
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {thermostats.map((thermostat) => (
            <ThermostatCard
              key={thermostat.id}
              device={thermostat}
              onUpdate={(newState) =>
                updateDeviceState(thermostat.id, newState)
              }
            />
          ))}
        </div>
      </section>

      {/* Locks Section */}
      <section>
        <h2 className="text-xl font-semibold text-white font-['Outfit'] mb-4 flex items-center gap-2">
          <Lock className="text-slate-400" size={24} />
          Locks
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {locks.map((lock) => (
            <LockCard
              key={lock.id}
              device={lock}
              onUpdate={(newState) => updateDeviceState(lock.id, newState)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function LightCard({ device, onUpdate }) {
  const { state, name, room } = device;
  const isOn = state.on;

  const toggleLight = () => {
    onUpdate({ ...state, on: !isOn });
    toast.success(`${name} turned ${isOn ? "off" : "on"}`);
  };

  const setBrightness = (value) => {
    onUpdate({ ...state, brightness: value[0] });
  };

  return (
    <div
      className={`device-card card-base p-5 ${isOn ? "active" : ""}`}
      style={{ "--device-color": isOn ? "#fbbf24" : "#475569" }}
      data-testid={`light-card-${device.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-white font-medium">{name}</h3>
          <p className="text-sm text-slate-500">{room}</p>
        </div>
        <button
          onClick={toggleLight}
          className={`p-3 rounded-full transition-all ${
            isOn
              ? "bg-yellow-500/20 text-yellow-400"
              : "bg-slate-800 text-slate-500"
          }`}
          data-testid={`toggle-light-${device.id}`}
        >
          <Lightbulb
            size={24}
            className={`light-icon ${isOn ? "on" : "off"}`}
          />
        </button>
      </div>

      {isOn && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">Brightness</span>
            <span className="text-sm text-white">{state.brightness}%</span>
          </div>
          <Slider
            value={[state.brightness]}
            onValueChange={setBrightness}
            max={100}
            step={5}
            className="w-full"
            data-testid={`brightness-slider-${device.id}`}
          />
        </div>
      )}

      <div className="mt-4 flex items-center justify-between">
        <span
          className={`text-xs font-medium ${
            isOn ? "text-yellow-400" : "text-slate-500"
          }`}
        >
          {isOn ? "ON" : "OFF"}
        </span>
        <Switch
          checked={isOn}
          onCheckedChange={toggleLight}
          data-testid={`switch-light-${device.id}`}
        />
      </div>
    </div>
  );
}

function ThermostatCard({ device, onUpdate }) {
  const { state, name, room } = device;
  const { temperature, target, mode } = state;

  const adjustTarget = (delta) => {
    const newTarget = Math.max(60, Math.min(85, target + delta));
    onUpdate({ ...state, target: newTarget });
  };

  const toggleMode = () => {
    const newMode = mode === "cool" ? "heat" : "cool";
    onUpdate({ ...state, mode: newMode });
    toast.success(`Switched to ${newMode}ing mode`);
  };

  const isHeating = mode === "heat";
  const isCooling = mode === "cool";

  return (
    <div
      className={`device-card card-base p-6 ${isHeating ? "heating" : ""} ${
        isCooling ? "cooling" : ""
      }`}
      style={{
        "--device-color": isHeating ? "#f43f5e" : "#06b6d4",
      }}
      data-testid={`thermostat-card-${device.id}`}
    >
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-white font-medium">{name}</h3>
          <p className="text-sm text-slate-500">{room}</p>
        </div>
        <button
          onClick={toggleMode}
          className={`p-2 rounded-lg transition-colors ${
            isHeating
              ? "bg-rose-500/20 text-rose-400"
              : "bg-cyan-500/20 text-cyan-400"
          }`}
          data-testid={`toggle-mode-${device.id}`}
        >
          {isHeating ? <Flame size={20} /> : <Snowflake size={20} />}
        </button>
      </div>

      <div className="flex items-center justify-center gap-8 mb-6">
        <div className="text-center">
          <p className="text-xs text-slate-500 uppercase mb-1">Current</p>
          <p className="text-3xl font-bold text-white">{temperature}°</p>
        </div>

        <div
          className={`thermostat-dial ${isHeating ? "heating" : "cooling"}`}
        >
          <div className="text-center">
            <p className="text-xs text-slate-400 uppercase">Target</p>
            <p className="text-2xl font-bold text-white">{target}°</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center gap-4">
        <button
          onClick={() => adjustTarget(-1)}
          className="w-12 h-12 rounded-full bg-slate-800 hover:bg-slate-700 text-white text-xl font-bold transition-colors"
          data-testid={`temp-down-${device.id}`}
        >
          -
        </button>
        <span className="text-slate-400 text-sm">Adjust Target</span>
        <button
          onClick={() => adjustTarget(1)}
          className="w-12 h-12 rounded-full bg-slate-800 hover:bg-slate-700 text-white text-xl font-bold transition-colors"
          data-testid={`temp-up-${device.id}`}
        >
          +
        </button>
      </div>

      <div className="mt-4 text-center">
        <span
          className={`text-xs font-medium uppercase ${
            isHeating ? "text-rose-400" : "text-cyan-400"
          }`}
        >
          {mode}ing Mode
        </span>
      </div>
    </div>
  );
}

function LockCard({ device, onUpdate }) {
  const { state, name, room } = device;
  const isLocked = state.locked;

  const toggleLock = () => {
    onUpdate({ locked: !isLocked });
    toast.success(`${name} ${isLocked ? "unlocked" : "locked"}`);
  };

  return (
    <div
      className={`device-card card-base p-5 ${isLocked ? "" : "active"}`}
      style={{ "--device-color": isLocked ? "#ef4444" : "#10b981" }}
      data-testid={`lock-card-${device.id}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-medium">{name}</h3>
          <p className="text-sm text-slate-500">{room}</p>
        </div>

        <button
          onClick={toggleLock}
          className={`p-4 rounded-full transition-all ${
            isLocked
              ? "bg-red-500/20 text-red-400"
              : "bg-green-500/20 text-green-400"
          }`}
          data-testid={`toggle-lock-${device.id}`}
        >
          {isLocked ? (
            <Lock size={28} className="lock-icon locked" />
          ) : (
            <Unlock size={28} className="lock-icon unlocked" />
          )}
        </button>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <span
          className={`text-sm font-medium ${
            isLocked ? "text-red-400" : "text-green-400"
          }`}
        >
          {isLocked ? "Locked" : "Unlocked"}
        </span>
        <Power
          size={16}
          className={isLocked ? "text-red-400" : "text-green-400"}
        />
      </div>
    </div>
  );
}
